// src/components/assets/BarcodeScanner.tsx
import { useState, useEffect, useRef } from 'react';
import { Html5Qrcode, Html5QrcodeScanType } from 'html5-qrcode';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';

interface BarcodeScannerProps {
  isOpen: boolean;
  onClose: () => void;
  onScan: (result: string) => void;
  mode: 'search' | 'sku';
}

export function BarcodeScanner({ isOpen, onClose, onScan, mode }: BarcodeScannerProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      initializeScanner();
    }
    return () => {
      stopScanner();
    };
  }, [isOpen]);

  const initializeScanner = async () => {
    setError(null);
    
    const config = {
      fps: 10,
      qrbox: { width: 250, height: 250 },
      supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
    };

    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
      } catch {
        // Ignore stop errors
      }
    }

    const scanner = new Html5Qrcode('barcode-reader');
    scannerRef.current = scanner;

    try {
      await scanner.start(
        { facingMode: 'environment' },
        config,
        (decodedText) => {
          onScan(decodedText);
          handleClose();
        },
        () => {
          // Scan errors are normal and frequent
        }
      );
    } catch (err) {
      setError('Camera access denied or not available. Please ensure camera permissions are granted.');
      console.error('Scanner error:', err);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current = null;
      } catch (err) {
        console.error('Error stopping scanner:', err);
        scannerRef.current = null;
      }
    }
  };

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === 'search' ? 'Scan to Find Item' : 'Scan Barcode/QR Code'}
      size="md"
      gradient="from-indigo-600 to-purple-600"
    >
      <div className="space-y-4">
        <p className="text-gray-700 text-center">
          {mode === 'search'
            ? 'Position the barcode or QR code in the frame to search for the item'
            : 'Position the barcode or QR code in the frame to capture the SKU'}
        </p>

        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Warning" size={20} />
              <span className="font-medium">Camera Error</span>
            </div>
            <p>{error}</p>
            <Button
              variant="secondary"
              size="sm"
              onClick={initializeScanner}
              className="mt-3"
            >
              Retry
            </Button>
          </div>
        ) : (
          <div
            id="barcode-reader"
            className="w-full rounded-lg overflow-hidden border-2 border-gray-300"
          />
        )}

        <p className="text-center text-sm text-gray-600">
          Supports: QR codes, barcodes (EAN, UPC, Code 128, etc.)
        </p>

        <Button variant="secondary" onClick={handleClose} className="w-full">
          Cancel
        </Button>
      </div>
    </Modal>
  );
}
